import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { BreadCrumbs } from '@/components/BreadCrumbs/BreadCrumbs'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { hivesOverviewOrganizationDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewOrganizationDetails'
import {
  type LicenseResponseBody,
  useGetOrgLicense,
} from '@/features/Licenses/api/licenseHooks'
import { ClusterBindingsPanel } from '@/features/Licenses/components/ClusterBindingsPanel'
import { EntitlementsPanel } from '@/features/Licenses/components/EntitlementsPanel'
import { HistoryPanel } from '@/features/Licenses/components/HistoryPanel'
import { LicenseActionDialog } from '@/features/Licenses/components/LicenseActionDialog'
import { LicenseOriginBadge } from '@/features/Licenses/components/LicenseOriginBadge'
import { LicenseStatusBadge } from '@/features/Licenses/components/LicenseStatusBadge'
import { MetadataPanel } from '@/features/Licenses/components/MetadataPanel'
import { useCanManageLicenses } from '@/features/Licenses/hooks/useCanManageLicenses'
import {
  type LicenseAction,
  type LicenseActionId,
  deriveActions,
} from '@/features/Licenses/lib/deriveActions'
import { useFindHivesByOrgId } from '@/generated/http-clients/v2ApiComponents'
import { hivesOverviewRootRoute } from '@/router/rootRoutes'
import {
  Box,
  Button,
  Grid,
  GridItem,
  HStack,
  Skeleton,
  Tooltip,
} from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { createRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

export const licenseDetailRoute = createRoute({
  getParentRoute: () => hivesOverviewRootRoute,
  path: '/orgs/$orgId/licenses/$licenseId',
  component: LicenseDetailPage,
  beforeLoad: ({ params }) => {
    const result = z.string().uuid().safeParse(params.licenseId)
    if (!result.success) {
      throw redirect({
        to: hivesOverviewOrganizationDetailsRoute.to,
        params: { orgId: params.orgId },
      })
    }
  },
})

type LicenseApiError = {
  status?: number
  payload?: { detail?: string; correlationId?: string }
  message?: string
}

export function LicenseDetailPage() {
  const { t } = useTranslation('features', { useSuspense: false })
  const { t: tCommon } = useTranslation(undefined, { useSuspense: false })
  const params = licenseDetailRoute.useParams()
  const { canManage, isResolved } = useCanManageLicenses()
  const navigate = useNavigate()
  const [openAction, setOpenAction] = useState<LicenseActionId | null>(null)

  const licenseQuery = useGetOrgLicense(
    { pathParams: { orgId: params.orgId, licenseId: params.licenseId } },
    {
      enabled: canManage,
      refetchInterval: 60_000,
    },
  )

  // Wait for `isResolved` before acting on a denial. The roles query is still
  // pending on the first render of a hard page load, which makes `canManage`
  // read false — redirecting on that would bounce every deep link (pasted URL,
  // refresh, new tab) straight back to Org Details, while in-app navigation
  // worked because the roles were already cached.
  useEffect(() => {
    if (isResolved && !canManage) {
      void navigate({
        to: hivesOverviewOrganizationDetailsRoute.to,
        params: { orgId: params.orgId },
      })
    }
  }, [canManage, isResolved, navigate, params.orgId])

  // Only for the org's name in the trail. Same query key the Org Details page
  // uses, so arriving from there costs nothing — and on a deep link it is one
  // request for a crumb that would otherwise read as an opaque id.
  const orgQuery = useFindHivesByOrgId({ pathParams: { orgId: params.orgId } })
  const orgName = orgQuery.data?.company_name?.trim()

  const license = licenseQuery.data as LicenseResponseBody | undefined

  const breadCrumbs = [
    {
      name: String(tCommon('sidebar.runningHives')),
      path: hivesOverviewRootRoute.to ?? '/',
    },
    {
      name: orgName || params.orgId,
      path:
        hivesOverviewOrganizationDetailsRoute.to?.replace(
          '$orgId',
          params.orgId,
        ) ?? `/orgs/${params.orgId}`,
    },
    {
      name: license?.name ?? params.licenseId,
      path: '',
    },
  ]

  // Render nothing while the redirect runs for unauthorized users — showing
  // the skeleton would briefly imply the license is being loaded, which is
  // misleading. The query is already disabled via `enabled: canManage`, so
  // there's no data fetch to expose either.
  if (isResolved && !canManage) {
    return null
  }
  // `!isResolved` means the roles are still loading, not that access was
  // denied — the skeleton is the honest state here, and the license query is
  // still disabled behind `enabled: canManage`.
  if (!isResolved || licenseQuery.isLoading) {
    return (
      <PageLayout
        title={String(t('licenses.detail.header.pageTitle'))}
        beforeTitle={<BreadCrumbs paths={breadCrumbs} />}
      >
        <Skeleton h="500px" w="100%" />
      </PageLayout>
    )
  }

  if (licenseQuery.isError) {
    const error = licenseQuery.error as LicenseApiError
    const is404 = error.status === 404
    let errorTitle: string
    if (is404) {
      errorTitle = String(t('licenses.detail.notFound.title'))
    } else {
      errorTitle = String(t('licenses.detail.error.title'))
    }
    let errorDescription: string | undefined
    if (!is404) {
      const baseDescription =
        error.payload?.detail ?? String(t('licenses.detail.error.description'))
      // 502 gateway errors get a correlation ID we expose for support — the
      // dialog forms already do this; mirror that contract on the detail
      // page error state so operators have a reference to share.
      const correlationId =
        error.status === 502 ? error.payload?.correlationId : undefined
      errorDescription = correlationId
        ? `${baseDescription} (${String(t('licenses.detail.error.correlationId'))} ${correlationId})`
        : baseDescription
    }
    return (
      <PageLayout
        title={String(t('licenses.detail.header.pageTitle'))}
        beforeTitle={<BreadCrumbs paths={breadCrumbs} />}
      >
        <ApiErrorState
          apiResource={licenseQuery as unknown as UseQueryResult}
          errorTitle={errorTitle}
          errorDescription={errorDescription}
        />
      </PageLayout>
    )
  }

  if (!license) {
    return null
  }

  const actions = deriveActions(license, t)
  const renew = actions.find((a) => a.id === 'renew')
  const reactivate = actions.find((a) => a.id === 'reactivate')
  const revoke = actions.find((a) => a.id === 'revoke')

  return (
    <PageLayout
      // A node, not a string: PageLayout runs string titles through `t()`,
      // which a user-supplied license name has no business passing through.
      title={
        <HStack gap={3} alignItems="center" flexWrap="wrap">
          <Box>{license.name ?? params.licenseId}</Box>
          {/* Status and origin sit with the name — they qualify the license
              as a whole, so they belong in the one place that names it. Sized
              up from the table default so they hold their own against a 4xl
              heading instead of reading as afterthoughts. */}
          <LicenseStatusBadge
            status={license.status}
            size="lg"
            fontWeight="bold"
          />
          {license.origin ? (
            <LicenseOriginBadge
              origin={license.origin}
              size="lg"
              fontWeight="bold"
            />
          ) : null}
        </HStack>
      }
      beforeTitle={<BreadCrumbs paths={breadCrumbs} />}
      action={
        <HStack gap={2}>
          {/* Renew is the primary CTA when surfaced (within 30d of expiry, or
              EXPIRED). It hides entirely far from expiry — see deriveActions. */}
          {renew?.visible ? (
            <ActionButton
              action={renew}
              colorPalette="brand"
              variant="solid"
              onAction={setOpenAction}
            />
          ) : null}
          {/* Reactivate surfaces on REVOKED/EXPIRED licenses that are not
              self-service. Always enabled when visible — deriveActions hides
              it for self-service licenses, which license-service refuses to
              reactivate. */}
          {reactivate?.visible ? (
            <ActionButton
              action={reactivate}
              colorPalette="brand"
              variant="solid"
              onAction={setOpenAction}
            />
          ) : null}
          {/* Revoke is always visible but disabled outside ACTIVE — operators
              try it on revoked / expired licenses; the disabled tooltip is
              the surface where they learn why. */}
          {revoke?.visible ? (
            <ActionButton
              action={revoke}
              colorPalette="red"
              variant="outline"
              onAction={setOpenAction}
            />
          ) : null}
        </HStack>
      }
    >
      {/* Three columns, read left to right: what is this license ("Details"),
          what does it grant ("Entitlements"), where is it actually in use
          ("Cluster bindings"). Stacks to one column below xl, where three
          columns would crush the UUID-length identifiers. */}
      <Grid
        w="100%"
        templateColumns={{ base: '1fr', xl: 'repeat(3, 1fr)' }}
        alignItems="stretch"
        gap={6}
      >
        <GridItem minW={0}>
          <MetadataPanel license={license} onAction={setOpenAction} />
        </GridItem>
        <GridItem minW={0}>
          <EntitlementsPanel license={license} onAction={setOpenAction} />
        </GridItem>
        <GridItem minW={0}>
          <ClusterBindingsPanel license={license} />
        </GridItem>
      </Grid>

      {/* Audit-log history for this license, in its own card below the details.
          Keyed by license so page + selection reset when switching licenses. */}
      <HistoryPanel key={params.licenseId} licenseId={params.licenseId} />

      <LicenseActionDialog
        action={openAction}
        license={license}
        onClose={() => setOpenAction(null)}
      />
    </PageLayout>
  )
}

type ActionButtonProps = {
  action: LicenseAction
  colorPalette: 'brand' | 'red'
  variant: 'solid' | 'outline'
  onAction: (id: LicenseActionId) => void
}

/**
 * Renders a page-header action button with the disabled-tooltip convention
 * applied uniformly: when the action is disabled we still render it so
 * operators can hover and read the reason, rather than silently hiding it.
 */
function ActionButton({
  action,
  colorPalette,
  variant,
  onAction,
}: Readonly<ActionButtonProps>) {
  const button = (
    <Button
      colorPalette={colorPalette}
      variant={variant}
      size="sm"
      disabled={!action.enabled}
      onClick={() => onAction(action.id)}
    >
      {action.label}
    </Button>
  )

  if (action.enabled || !action.actionDisabledReason) {
    return button
  }

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <Box>{button}</Box>
      </Tooltip.Trigger>
      <Tooltip.Positioner>
        <Tooltip.Content>
          <Tooltip.Arrow />
          {action.actionDisabledReason}
        </Tooltip.Content>
      </Tooltip.Positioner>
    </Tooltip.Root>
  )
}
